/**
 * WebRewardsPageService - Web Platform Implementation
 *
 * Web-specific implementation of IRewardsPageService
 * Integrates with Zustand rewards store
 */

import type {
  IRewardsPageService,
  RewardBalance,
  AvailableReward,
  RewardTransaction,
  RewardRedemptionResult
} from '../../contracts'
import type { RewardTier } from '@restaurant/core'
import { useRewardsStore } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'
import { safeGetLogger } from '@restaurant/logging'


/** Shape of a single row from Flask's GET /reward_transactions */
interface FlaskRewardTransaction {
  id: string
  user_id?: string
  restaurant_id?: string
  transaction_type: 'EARN' | 'REDEEM' | 'EXPIRE' | (string & {})
  points_change: number
  notes?: string
  order_id?: string
  transaction_date?: string
  created_at?: string
}

/** Shape of Flask's GET /customer_rewards/{merchantId}/user/{userId} */
interface FlaskCustomerRewardResponse {
  id?: string
  current_points?: number
  currentPoints?: number
}

// TODO: replace with backend tier data when available
const TIER_THRESHOLDS: Record<RewardTier, number> = {
  bronze: 0,
  silver: 500,
  gold: 1500,
  platinum: 3000,
} as const

/**
 * Web implementation of rewards page service
 * Uses Zustand store that's populated by useRewardsData hook
 */
export class WebRewardsPageService implements IRewardsPageService {
  /**
   * Get user's current reward balance and tier information
   */
  async getUserRewardBalance(_userId: string, _restaurantId: string): Promise<RewardBalance> {
    const { balance } = useRewardsStore.getState()

    // Tier and tierProgress remain hardcoded — Flask API does not return
    // tier thresholds in MerchantRewardConfigResponse or customer_rewards.
    // When the backend adds tier support, wire it here.
    return {
      currentPoints: balance,
      lifetimePoints: balance, // placeholder — Flask API does not return cumulative earned points
      tier: 'bronze',
      tierProgress: 0.5,
      nextTier: 'silver',
      pointsToNextTier: Math.max(0, TIER_THRESHOLDS.silver - balance),
      expiringPoints: 0,
      expiringDate: undefined,
    }
  }

  /**
   * Get all available rewards for redemption at a restaurant.
   * Note: For item-based merchants, availableRewards (from redemptionOptions) is
   * typically empty — item groups live in merchantConfig.item_redemption_groups.
   * The active web ViewModel bypasses this method and reads groups directly.
   * Future consumers (e.g., native app) should check item_redemption_groups too.
   */
  async getAvailableRewards(_restaurantId: string): Promise<AvailableReward[]> {
    try {
      const { availableRewards } = useRewardsStore.getState()

      // Transform RedemptionOption to AvailableReward format
      const rewards: AvailableReward[] = availableRewards.map((redemption) => ({
        id: redemption.id,
        restaurantId: _restaurantId,
        name: redemption.name,
        description: redemption.description || '',
        pointsCost: redemption.pointsCost,
        value: redemption.value,
        type: redemption.type,
        imageUrl: undefined,
        isActive: redemption.isActive,
        requiresMinimumTier: undefined,
        availableQuantity: undefined,
      }))

      return rewards
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get available rewards', { domain: 'reward', error: String(error) })
      throw new Error('Failed to load available rewards')
    }
  }

  /**
   * Get user's reward transaction history
   */
  async getRewardHistory(_userId: string, _restaurantId: string, limit?: number): Promise<RewardTransaction[]> {
    const { customerRewardId } = useRewardsStore.getState()

    if (!customerRewardId) {
      return []
    }

    try {
      const params: Record<string, string | number> = { customer_reward_id: customerRewardId }
      if (limit) params.limit = limit

      const response = await apiClient.get<FlaskRewardTransaction[]>('/reward_transactions', { params })

      // Map Flask snake_case response to contract's camelCase RewardTransaction shape
      // Validate each row and skip malformed entries
      const transactions: RewardTransaction[] = []
      for (const t of response || []) {
        if (!t.id || !t.transaction_type) {
          const logger = safeGetLogger()
          logger?.warn('Skipping malformed reward transaction', { domain: 'reward' })
          continue
        }

        transactions.push({
          id: t.id,
          userId: t.user_id || _userId,
          restaurantId: t.restaurant_id || _restaurantId,
          type: t.transaction_type.toLowerCase() === 'earn' ? 'earned'
            : t.transaction_type.toLowerCase() === 'redeem' ? 'redeemed'
            : t.transaction_type.toLowerCase() === 'expire' ? 'expired'
            : 'adjusted',
          points: t.points_change ?? 0,
          description: t.notes || `${t.transaction_type} transaction`,
          orderId: t.order_id,
          createdAt: t.transaction_date || t.created_at || new Date().toISOString(),
        })
      }

      // Store in Zustand for later access
      useRewardsStore.getState().setTransactions(transactions)

      return transactions
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to get reward history', { domain: 'reward', error: String(error) })
      return []
    }
  }

  /**
   * Redeem a reward for the user
   */
  async redeemReward(rewardId: string, _userId: string): Promise<RewardRedemptionResult> {
    // Note: WebRewardsPageViewModel (active web ViewModel) handles redemptions
    // directly via handleRedemption() and does NOT call this method.
    // This is wired for the base RewardsPageViewModel contract and future native app use.
    const store = useRewardsStore.getState()
    const { availableRewards, merchantConfig } = store

    // Search availableRewards (dollar-based merchants use redemptionOptions)
    let points: number | undefined
    let groupId: string | undefined
    let itemId: string | undefined

    const reward = availableRewards.find((r) => r.id === rewardId)
    if (reward) {
      points = reward.pointsCost
      groupId = rewardId
    } else {
      // Fall back to item_redemption_groups (item-based merchants)
      const groups = merchantConfig?.item_redemption_groups || []
      for (const group of groups) {
        const item = group.items.find((i) => i.id === rewardId)
        if (item) {
          points = group.point_value
          groupId = group.id
          itemId = item.id
          break
        }
      }
    }

    if (points === undefined || !groupId) {
      return { success: false, error: 'Reward not found' }
    }

    if (!store.canAffordReward(points)) {
      return { success: false, error: 'Insufficient points' }
    }

    store.addPendingRedemption(points, groupId, itemId)

    return {
      success: true,
      // Optimistic remaining points (not yet committed — actual deduction happens in place_order)
      newBalance: useRewardsStore.getState().getRemainingPoints(),
    }
  }

  /**
   * Remove a pending reward redemption
   */
  unredeemReward(rewardId: string): void {
    useRewardsStore.getState().removePendingRedemption(rewardId)
  }

  /**
   * Check if a reward is currently redeemed (pending in cart)
   */
  isRewardRedeemed(rewardId: string): boolean {
    return useRewardsStore.getState().appliedRewardDetails.some((d) => d.groupId === rewardId)
  }

  /**
   * Get remaining points after pending redemptions
   */
  getRemainingPoints(): number {
    return useRewardsStore.getState().getRemainingPoints()
  }

  /**
   * Search rewards by name or description
   */
  searchRewards(rewards: AvailableReward[], query: string): AvailableReward[] {
    const lowercaseQuery = query.toLowerCase().trim()

    if (!lowercaseQuery) {
      return rewards
    }

    return rewards.filter(reward =>
      reward.name.toLowerCase().includes(lowercaseQuery) ||
      reward.description.toLowerCase().includes(lowercaseQuery)
    )
  }

  /**
   * Filter rewards by tier accessibility
   */
  filterRewardsByTier(rewards: AvailableReward[], tier: RewardTier): AvailableReward[] {
    // Bronze threshold is 0 — || lets 0 fall through to Infinity so all rewards remain visible.
    // Use || (not ??) intentionally: 0 is falsy and correctly maps to "no upper bound" for bronze.
    const maxPoints = TIER_THRESHOLDS[tier] || Infinity
    return rewards.filter(reward => reward.pointsCost <= maxPoints)
  }

  /**
   * Navigate to reward details page
   */
  navigateToReward(rewardId: string): void {
    // In a real implementation, this would trigger navigation
    // For now, we'll just update the URL using browser history

    // In a React Router context, this would use navigate()
    // For this implementation, we'll just update the URL
    window.history.pushState({}, '', `/rewards/${rewardId}`)
  }

  /**
   * Refresh user's reward data (force reload)
   */
  async refreshUserRewards(userId: string, _restaurantId: string): Promise<RewardBalance> {
    // Method signature takes restaurantId, but API needs merchantId.
    // Read from merchantConfig.merchant_id[0] (same translation useRewardsData does).
    const { merchantConfig } = useRewardsStore.getState()
    const merchantId = merchantConfig?.merchant_id?.[0]

    if (merchantId && userId) {
      try {
        const response = await apiClient.get<FlaskCustomerRewardResponse>(
          `/customer_rewards/${merchantId}/user/${userId}`
        )
        const currentPoints = response.current_points ?? response.currentPoints ?? 0
        useRewardsStore.getState().setBalance(currentPoints)
        useRewardsStore.getState().setCustomerRewardId(response.id ?? null)
      } catch (e) {
        const logger = safeGetLogger()
        logger?.warn('refreshUserRewards fetch failed — keeping stale balance', { domain: 'reward' })
      }
    }

    return this.getUserRewardBalance(userId, _restaurantId)
  }
}
